'use client';

import { useState, useEffect } from 'react';
import { VRMExpressionPresetName } from '@pixiv/three-vrm';
import { VrmManager } from '@/lib/vrm/VrmManager';
import styles from './VRMControlPanel.module.scss';

interface VRMControlPanelProps {
  className?: string;
}

interface AnimationOption {
  label: string;
  path: string;
}

interface ModelOption {
  label: string;
  path: string;
}

// アニメーション名は Readme_VRMA_MotionPack_JP.txt に準拠
const ANIMATION_OPTIONS: AnimationOption[] = [
  { label: '挨拶', path: '/animations/VRMA_MotionPack/vrma/VRMA_02.vrma' },
  { label: '全身を見せる', path: '/animations/VRMA_MotionPack/vrma/VRMA_01.vrma' },
  { label: 'Vサイン', path: '/animations/VRMA_MotionPack/vrma/VRMA_03.vrma' },
  { label: '撃つ', path: '/animations/VRMA_MotionPack/vrma/VRMA_04.vrma' },
  { label: '回る', path: '/animations/VRMA_MotionPack/vrma/VRMA_05.vrma' },
  { label: 'モデルポーズ', path: '/animations/VRMA_MotionPack/vrma/VRMA_06.vrma' },
  { label: '屈伸運動', path: '/animations/VRMA_MotionPack/vrma/VRMA_07.vrma' },
  { label: 'Bling-Bang-Bang-Born', path: '/animations/八ツ橋まろん/vrma/Bling-Bang-Bang-Born.vrma' },
  { label: 'シカ色デイズ', path: '/animations/八ツ橋まろん/vrma/シカ色デイズ.vrma' },
  { label: 'ブレイクダンス', path: '/animations/Mixamo/vrma/Breakdance-Freezes.vrma' }
];

const MODEL_OPTIONS: ModelOption[] = [
  { label: '青波響', path: '/models/character-01.vrm' },
  { label: '春野翠', path: '/models/character-02.vrm' }
];

// Safe access to MODEL_OPTIONS and ANIMATION_OPTIONS
const DEFAULT_MODEL = MODEL_OPTIONS[0]?.path || '/models/character-01.vrm';
const DEFAULT_ANIMATION = ANIMATION_OPTIONS[0]?.path || '/animations/VRMA_MotionPack/vrma/VRMA_02.vrma';

const EXPRESSION_OPTIONS = [
  { label: 'ニュートラル', name: VRMExpressionPresetName.Neutral },
  { label: '喜び', name: VRMExpressionPresetName.Happy },
  { label: '怒り', name: VRMExpressionPresetName.Angry },
  { label: '悲しみ', name: VRMExpressionPresetName.Sad },
  { label: '驚き', name: VRMExpressionPresetName.Surprised },
  { label: 'リラックス', name: VRMExpressionPresetName.Relaxed }
];

export default function VRMControlPanel({ className = '' }: VRMControlPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [selectedAnimation, setSelectedAnimation] = useState(DEFAULT_ANIMATION);
  const [expressionValues, setExpressionValues] = useState<Record<VRMExpressionPresetName, number>>(() => {
    const initial: Partial<Record<VRMExpressionPresetName, number>> = {};
    EXPRESSION_OPTIONS.forEach((exp) => {
      initial[exp.name] = 0;
    });
    return initial as Record<VRMExpressionPresetName, number>;
  });
  const [vrmManager, setVrmManager] = useState<VrmManager | null>(null);

  useEffect(() => {
    // Get VRM manager instance after component mounts
    const manager = VrmManager.getInstance();
    setVrmManager(manager);
  }, []);

  const handleModelChange = async (modelPath: string) => {
    setSelectedModel(modelPath);
    if (vrmManager && vrmManager.isInitialized) {
      await vrmManager.loadModel(modelPath);
      // Reload current animation after model change
      await vrmManager.loadAnimation(selectedAnimation);
    }
  };

  const handleAnimationChange = async (animationPath: string) => {
    setSelectedAnimation(animationPath);
    if (vrmManager && vrmManager.isInitialized) {
      await vrmManager.loadAnimation(animationPath);
    }
  };

  const handleExpressionSliderChange = (expressionName: VRMExpressionPresetName, value: number) => {
    setExpressionValues((prev) => ({ ...prev, [expressionName]: value }));
    if (vrmManager && vrmManager.isInitialized) {
      vrmManager.setExpression(expressionName, value);
    }
  };

  const handleCameraReset = () => {
    if (vrmManager) {
      vrmManager.resetCamera();
    }
  };

  const handleCameraMove = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (!vrmManager) {
      return;
    }
    
    const moveAmount = 50;
    switch (direction) {
      case 'up':
        vrmManager.panCamera(0, -moveAmount);  // 逆にする
        break;
      case 'down':
        vrmManager.panCamera(0, moveAmount);   // 逆にする
        break;
      case 'left':
        vrmManager.panCamera(-moveAmount, 0);
        break;
      case 'right':
        vrmManager.panCamera(moveAmount, 0);
        break;
    }
  };

  const handleCameraRotate = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (!vrmManager) {
      return;
    }
    
    const rotateAmount = 20;
    switch (direction) {
      case 'up':
        vrmManager.rotateCamera(0, -rotateAmount);
        break;
      case 'down':
        vrmManager.rotateCamera(0, rotateAmount);
        break;
      case 'left':
        vrmManager.rotateCamera(-rotateAmount, 0);
        break;
      case 'right':
        vrmManager.rotateCamera(rotateAmount, 0);
        break;
    }
  };

  const handleCameraZoom = (direction: 'in' | 'out') => {
    if (!vrmManager) {
      return;
    }
    vrmManager.zoomCamera(direction === 'in' ? -1 : 1);
  };

  return (
    <div className={`${styles.controlPanel} ${className} ${isOpen ? styles.open : ''}`}>
      <button
        className={styles.toggleButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'コントロールパネルを閉じる' : 'コントロールパネルを開く'}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" strokeWidth="2"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" strokeWidth="2"/>
        </svg>
        <span className={styles.toggleLabel}>
          {isOpen ? '閉じる' : 'VRM設定'}
        </span>
      </button>

      <div className={styles.panelContent}>
        <h3 className={styles.panelTitle}>VRMコントロールパネル</h3>

        {/* Model Selection */}
        <div className={styles.controlGroup}>
          <label htmlFor="model-select" className={styles.controlLabel}>キャラクター</label>
          <select
            id="model-select"
            className={styles.select}
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
          >
            {MODEL_OPTIONS.map(model => (
              <option key={model.path} value={model.path}>
                {model.label}
              </option>
            ))}
          </select>
        </div>

        {/* Animation Selection */}
        <div className={styles.controlGroup}>
          <label htmlFor="animation-select" className={styles.controlLabel}>モーション</label>
          <select
            id="animation-select"
            className={styles.select}
            value={selectedAnimation}
            onChange={(e) => handleAnimationChange(e.target.value)}
          >
            {ANIMATION_OPTIONS.map(animation => (
              <option key={animation.path} value={animation.path}>
                {animation.label}
              </option>
            ))}
          </select>
        </div>

        {/* Expression Sliders */}
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>表情コントロール</span>
          {EXPRESSION_OPTIONS.map((expression) => (
            <div key={expression.name} className={styles.sliderRow}>
              <label className={styles.sliderLabel}>{expression.label}</label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={expressionValues[expression.name] ?? 0}
                onChange={(e) => handleExpressionSliderChange(expression.name, parseFloat(e.target.value))}
                className={styles.slider}
              />
            </div>
          ))}
        </div>

        {/* Camera Controls */}
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>カメラ操作</span>
          
          {/* Camera Movement */}
          <div className={styles.cameraControlSection}>
            <span className={styles.controlSubLabel}>移動</span>
            <div className={styles.controlPad}>
              <button
                className={`${styles.controlButton} ${styles.up}`}
                onClick={() => handleCameraMove('up')}
                aria-label="カメラ上移動"
                title="上に移動"
              >
                ▲
              </button>
              <button
                className={`${styles.controlButton} ${styles.left}`}
                onClick={() => handleCameraMove('left')}
                aria-label="カメラ左移動"
                title="左に移動"
              >
                ◀
              </button>
              <button
                className={`${styles.controlButton} ${styles.center}`}
                disabled
              >
                ✦
              </button>
              <button
                className={`${styles.controlButton} ${styles.right}`}
                onClick={() => handleCameraMove('right')}
                aria-label="カメラ右移動"
                title="右に移動"
              >
                ▶
              </button>
              <button
                className={`${styles.controlButton} ${styles.down}`}
                onClick={() => handleCameraMove('down')}
                aria-label="カメラ下移動"
                title="下に移動"
              >
                ▼
              </button>
            </div>
          </div>
          
          {/* Camera Rotation */}
          <div className={styles.cameraControlSection}>
            <span className={styles.controlSubLabel}>回転</span>
            <div className={styles.controlPad}>
              <button
                className={`${styles.controlButton} ${styles.up}`}
                onClick={() => handleCameraRotate('up')}
                aria-label="カメラ上回転"
                title="上に回転"
              >
                ⟲
              </button>
              <button
                className={`${styles.controlButton} ${styles.left}`}
                onClick={() => handleCameraRotate('left')}
                aria-label="カメラ左回転"
                title="左に回転"
              >
                ↶
              </button>
              <button
                className={`${styles.controlButton} ${styles.center}`}
                disabled
              >
                ⦿
              </button>
              <button
                className={`${styles.controlButton} ${styles.right}`}
                onClick={() => handleCameraRotate('right')}
                aria-label="カメラ右回転"
                title="右に回転"
              >
                ↷
              </button>
              <button
                className={`${styles.controlButton} ${styles.down}`}
                onClick={() => handleCameraRotate('down')}
                aria-label="カメラ下回転"
                title="下に回転"
              >
                ⟳
              </button>
            </div>
          </div>
          
          {/* Zoom Controls */}
          <div className={styles.cameraControlSection}>
            <span className={styles.controlSubLabel}>ズーム</span>
            <div className={styles.zoomControls}>
              <button
                className={styles.zoomButton}
                onClick={() => handleCameraZoom('out')}
                aria-label="ズームアウト"
                title="ズームアウト"
              >
                −
              </button>
              <span className={styles.zoomLabel}>拡大/縮小</span>
              <button
                className={styles.zoomButton}
                onClick={() => handleCameraZoom('in')}
                aria-label="ズームイン"
                title="ズームイン"
              >
                +
              </button>
            </div>
          </div>
          
          {/* Reset Button */}
          <button
            className={styles.resetButton}
            onClick={handleCameraReset}
            title="カメラを初期位置に戻す"
          >
            🔄 カメラリセット
          </button>
        </div>
      </div>
    </div>
  );
}